import React, {useState} from 'react';

import {Button, EditIcon, OutfitIcon, Progress, ProgressColor, Select} from '@kit';

import {cls} from '../../../../components/utils';
import {useCharacter} from '../character-context';
import {ATTUNEMENT_SLOTS} from '../model/character';
import {EquippedItem, isEquippable, isWeapon} from '../model/equipment';
import {CoinsBlock} from './coins-block';
import {EquipmentManagement} from './equipment-management';
import {EquipmentTable} from './equipment-row';
import {FilterChip, FilterChips} from './filter-chips';
import {formatPounds} from './format';
import {PaperDollModal} from './paper-doll-modal';
import {StringListEditor} from './string-list-editor';
import {useWideScreen} from './use-wide-screen';
import {WeightModal} from './weight-modal';

import css from './sheet.module.css';

type EquipmentFilter = 'all' | 'equipped' | 'weapons' | 'armor' | 'other';

const FILTERS: {value: EquipmentFilter; label: string; matches(equipped: EquippedItem): boolean}[] = [
	{value: 'all', label: 'Все', matches: () => true},
	{value: 'equipped', label: 'Используется', matches: ({entry}) => !!entry.equipped},
	{value: 'weapons', label: 'Оружие', matches: ({item}) => isWeapon(item)},
	{value: 'armor', label: 'Доспехи и щиты', matches: ({item}) => !!item.armorKind},
	{value: 'other', label: 'Прочее', matches: ({item}) => !isEquippable(item)},
];

export const EquipmentTab: React.FC = () => {
	const [isWeightOpen, setWeightOpen] = useState(false);

	return (
		<>
			<div className={css.tabToolbar}>
				<WeightButton onClick={() => setWeightOpen(true)} />
				<CoinsBlock />
			</div>
			{isWeightOpen && <WeightModal onClose={() => setWeightOpen(false)} />}
			<EquipmentSection />
			<AttunementSection />
			<section className={css.tabSection}>
				<h3 className={css.groupTitle}>Другие вещи</h3>
				<OtherItems />
			</section>
		</>
	);
};

const WeightButton: React.FC<{onClick(): void}> = ({onClick}) => {
	const {character} = useCharacter();
	const weight = character.carriedWeight;
	const {carry, pushDragLift} = character.carryingCapacity;
	const color: ProgressColor = weight <= carry ? 'success' : weight <= pushDragLift ? 'warning' : 'error';
	return (
		<Button
			appearance="subtle"
			className={css.weightButton}
			title={`Переносимый вес: ${formatPounds(carry)}; толкать, тянуть или поднимать: ${formatPounds(
				pushDragLift,
			)}`}
			onClick={onClick}
		>
			<span>{formatPounds(weight)}</span>
			<Progress
				className={cls(css.weightBar, color === 'warning' && css.weightCaution)}
				color={color}
				percentComplete={pushDragLift > 0 ? Math.min(100, (weight / pushDragLift) * 100) : 0}
			/>
		</Button>
	);
};

const EquipmentSection: React.FC = () => {
	const {character} = useCharacter();
	const [filter, setFilter] = useState<EquipmentFilter>('all');
	const [isManaging, setManaging] = useState(false);
	const [isDollOpen, setDollOpen] = useState(false);
	const isWide = useWideScreen();
	const {equipment} = character;

	const chips: FilterChip<EquipmentFilter>[] = FILTERS.map(({value, label, matches}) => ({
		value,
		label,
		count: value === 'all' ? undefined : equipment.filter(matches).length,
	}));
	const matches = FILTERS.find(({value}) => value === filter)!.matches;

	return (
		<section className={css.tabSection}>
			<h3 className={css.groupTitle}>Снаряжение</h3>
			<div className={css.tabToolbar}>
				<FilterChips<EquipmentFilter> label="Вид предметов" items={chips} value={filter} onChange={setFilter} />
				<div className={css.toolbarButtons}>
					<Button
						icon={<OutfitIcon />}
						title={isWide ? undefined : 'Экипировка'}
						aria-label={isWide ? undefined : 'Экипировка'}
						onClick={() => setDollOpen(true)}
					>
						{isWide && 'Экипировка'}
					</Button>
					<Button
						icon={<EditIcon />}
						title={isWide ? undefined : 'Управление снаряжением'}
						aria-label={isWide ? undefined : 'Управление снаряжением'}
						onClick={() => setManaging(true)}
					>
						{isWide && 'Управление снаряжением'}
					</Button>
				</div>
			</div>
			{isDollOpen && <PaperDollModal onClose={() => setDollOpen(false)} />}
			{equipment.length ? (
				<EquipmentTable items={equipment.filter(matches)} />
			) : (
				<p className={css.muted}>Добавьте предметы через «Управление снаряжением».</p>
			)}
			<EquipmentManagement open={isManaging} onClose={() => setManaging(false)} />
		</section>
	);
};

const AttunementSection: React.FC = () => {
	const {state, character, update} = useCharacter();
	const items = character.equipment
		.filter(({item}) => item.requiresAttunement)
		.map(({entry, item}) => ({value: entry.key, label: item.title}));
	const slots = Array.from({length: ATTUNEMENT_SLOTS}, (_, index) => state.attunement[index]);

	const setSlot = (index: number, entryKey: string | undefined) =>
		update((current) => {
			const attunement = Array.from({length: ATTUNEMENT_SLOTS}, (_, slot) => current.attunement[slot]);
			attunement[index] = entryKey as string;
			return {attunement: attunement.filter(Boolean)};
		});

	return (
		<section className={css.tabSection}>
			<h3 className={css.groupTitle}>Настроенность</h3>
			{!items.length && <p className={css.muted}>В снаряжении нет предметов, требующих настройки.</p>}
			<div className={css.attunement}>
				{slots.map((entryKey, index) => (
					<Select
						key={index}
						items={items.filter(
							(item) => item.value === entryKey || !state.attunement.includes(item.value),
						)}
						value={entryKey ?? null}
						placeholder="Свободный слот"
						onChange={([value]) => setSlot(index, value as string | undefined)}
					/>
				))}
			</div>
		</section>
	);
};

const OtherItems: React.FC = () => {
	const {state, update} = useCharacter();
	return (
		<StringListEditor
			values={state.otherItems}
			placeholder="Например, письмо от наставника"
			onChange={(otherItems) => update({otherItems})}
		/>
	);
};
